import { Component, computed, HostBinding, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import {
  ObjectiveComparison,
  ReductionTipsService,
} from '../../services/reduction-tips.service';
import { COMMITMENT_STATUS_STYLE } from '../../constants/commitment-status-style';

/**
 * Colours of the objective badge, by how far the trip is above it. The bar
 * itself stays grey: the badge already carries the verdict, and three
 * coloured bars made the section shout.
 */
const OBJECTIVE_BADGE_STYLE = {
  reached: 'bg-validation-100 text-validation-700',
  close: 'bg-warning-100 text-warning-700',
  far: 'bg-error-100 text-error-700',
};

/** Above that many % over the objective, the trip reads as far from it. */
const FAR_FROM_OBJECTIVE_PERCENT = 50;

/**
 * Where the trip stands against what the company set itself: each of its
 * objectives a single trip can be measured on (per traveller, per traveller
 * and day, per 1 000 € of price), and each reduction action it committed to. The
 * tips that would close the gap are listed under each stats category.
 */
@Component({
  selector: 'app-trip-goals',
  standalone: true,
  imports: [TranslocoModule, LucideAngularModule],
  templateUrl: './trip-goals.component.html',
})
export class TripGoalsComponent {
  /** Block, or the page's `space-y-*` cannot put a margin above it. */
  @HostBinding('class') readonly hostClass = 'block';

  protected tipsService = inject(ReductionTipsService);
  protected formatCo2e = inject(FormatCo2eService);

  readonly statusStyle = COMMITMENT_STATUS_STYLE;

  commitments = this.tipsService.commitmentDetails;

  /** This trip against each of the company's active objectives. */
  objectives = computed(() => {
    const total = this.tipsService.tripTotal();
    return total === null ? [] : this.tipsService.compareToObjectives(total);
  });

  visible = computed(
    () => this.objectives().length > 0 || this.commitments().length > 0,
  );

  bothCards = computed(
    () => this.objectives().length > 0 && this.commitments().length > 0,
  );

  /** The bar is scaled so the highest of the two values never fills it. */
  private scale(objective: ObjectiveComparison): number {
    return Math.max(objective.value, objective.target) * 1.2;
  }

  barWidthPercent(objective: ObjectiveComparison): number {
    return Math.round((objective.value / this.scale(objective)) * 100);
  }

  markerPercent(objective: ObjectiveComparison): number {
    return Math.round((objective.target / this.scale(objective)) * 100);
  }

  badgeStyle(objective: ObjectiveComparison): string {
    if (!objective.isAboveTarget) return OBJECTIVE_BADGE_STYLE.reached;
    return objective.differencePercent <= FAR_FROM_OBJECTIVE_PERCENT
      ? OBJECTIVE_BADGE_STYLE.close
      : OBJECTIVE_BADGE_STYLE.far;
  }
}
